// client/src/scenes/MainMenuScene.js

import Phaser from 'phaser';
import SoundManager from '../utils/soundManager';

class MainMenuScene extends Phaser.Scene {
    constructor() {
        super({ key: 'MainMenuScene' });
        this.soundManager = null;
    }

    preload() {
        // Base64 encoded empty MP3 sound
        const emptySound = 'data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA/+M4wAAAAAAAAAAAAEluZm8AAAAPAAAAAwAAAboBzc3Nzc3Nzc3Nzc3Nzc3Nzc3Nzc3Nzc3Nzc3NzbOzs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7O/////////////////////////';

        // Load audio with base64
        this.load.audio('menu-music', emptySound);
        this.load.audio('button-click', emptySound);
        this.load.audio('slap-sound', emptySound);
        this.load.audio('punch-sound', emptySound);
        this.load.audio('shoot-sound', emptySound);
        this.load.audio('combo-sound', emptySound);
        this.load.audio('success-sound', emptySound);
        this.load.audio('error-sound', emptySound);
        this.load.audio('camera-sound', emptySound);

        // Base64 encoded 1x1 pixel images for placeholders
        const redPixel = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8z8BQDwAEhQGAhKmMIQAAAABJRU5ErkJggg==';
        const greenPixel = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==';
        const bluePixel = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==';
        const yellowPixel = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8z/C/HgAGgwJ/lK3Q6wAAAABJRU5ErkJggg==';
        const whitePixel = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8/x8AAwMCAO+ip1sAAAAASUVORK5CYII=';

        // Load these placeholder pixels for various images
        this.load.image('flat-hand-placeholder', greenPixel);
        this.load.image('fist-placeholder', redPixel);
        this.load.image('finger-guns-placeholder', yellowPixel);
        this.load.image('target-placeholder', whitePixel);
        this.load.image('game-bg-placeholder', bluePixel);
        this.load.image('menu-background', bluePixel);
        this.load.image('camera-icon', yellowPixel);
        this.load.image('check-icon', greenPixel);
        this.load.image('error-icon', redPixel);
        this.load.image('back-button', redPixel);
        this.load.image('button', greenPixel);
        this.load.image('menu-button', bluePixel);
        this.load.image('replay-button', greenPixel);
        this.load.image('logo', whitePixel);
    }

    // Additionally, use this utility function in appropriate scenes
    createSimpleSprite(scene, x, y, size, color) {
        // Create a circle sprite with the given parameters
        return scene.add.circle(x, y, size, color);
    }

    // Simplify the create method
    create() {
        // Add background
        this.add.rectangle(400, 300, 800, 600, 0x000000);

        // Add title
        this.add.text(400, 150, 'Political Hand Gesture Game', {
            fontFamily: 'Arial',
            fontSize: 36,
            color: '#ffffff'
        }).setOrigin(0.5);

        // Add subtitle
        this.add.text(400, 200, 'A satirical, politically irreverent game', {
            fontFamily: 'Arial',
            fontSize: 20,
            color: '#cccccc',
            fontStyle: 'italic'
        }).setOrigin(0.5);

        // Create menu buttons
        this.createButton(400, 300, 'Start Game', () => this.startGame());
        this.createButton(400, 370, 'How to Play', () => this.showInstructions());
        this.createButton(400, 440, 'Credits', () => this.showCredits());

        // Add settings button (optional but keeps UI consistent)
        this.createSettingsButton();

        // Add tagline
        this.add.text(400, 550, '"Where your gestures speak louder than words"', {
            fontFamily: 'Arial',
            fontSize: 16,
            fontStyle: 'italic',
            color: '#999999'
        }).setOrigin(0.5);

        console.log('MainMenuScene created successfully');
    }

    createStartButton() {
        // Create button background
        const button = this.add.rectangle(400, 350, 200, 50, 0x4CAF50);
        button.setInteractive({ useHandCursor: true });

        // Add button text
        const buttonText = this.add.text(400, 350, 'Start Game', {
            fontFamily: 'Arial',
            fontSize: 20,
            color: '#ffffff'
        }).setOrigin(0.5);

        // Add hover effect
        button.on('pointerover', () => {
            button.fillColor = 0x66BB6A;
        });

        button.on('pointerout', () => {
            button.fillColor = 0x4CAF50;
        });

        // Add click effect
        button.on('pointerdown', () => {
            button.fillColor = 0x388E3C;
        });

        button.on('pointerup', () => {
            button.fillColor = 0x4CAF50;
            this.startGame();
        });
    }

    // Add this method to create a settings button
    createSettingsButton() {
        try {
            // Create button at the top-right
            const settingsButton = this.add.circle(750, 50, 25, 0x666666);
            settingsButton.setInteractive({ useHandCursor: true });

            // Add gear icon (or text if emoji doesn't render)
            const gear = this.add.text(750, 50, '⚙', {
                fontSize: 24
            }).setOrigin(0.5);

            // Add hover effect
            settingsButton.on('pointerover', () => {
                settingsButton.fillColor = 0x888888;
            });

            settingsButton.on('pointerout', () => {
                settingsButton.fillColor = 0x666666;
            });

            // Handle click - show a simple settings panel
            settingsButton.on('pointerup', () => {
                // Create a simple overlay
                const overlay = this.add.rectangle(400, 300, 600, 400, 0x000000, 0.9);

                // Add title
                this.add.text(400, 150, 'SETTINGS', {
                    fontFamily: 'Arial',
                    fontSize: 28,
                    color: '#ffffff'
                }).setOrigin(0.5);

                // Add placeholder settings
                this.add.text(400, 230, 'Settings will be fully implemented in the next version.', {
                    fontFamily: 'Arial',
                    fontSize: 16,
                    color: '#ffffff',
                    align: 'center'
                }).setOrigin(0.5);

                // Add instruction text
                this.add.text(400, 270, 'Camera access is processed locally on your device\nNo data is sent to any server', {
                    fontFamily: 'Arial',
                    fontSize: 14,
                    color: '#aaaaaa',
                    align: 'center'
                }).setOrigin(0.5);

                // Add close button
                const closeButton = this.add.rectangle(400, 350, 150, 40, 0xF44336);
                closeButton.setInteractive();

                const closeText = this.add.text(400, 350, 'Close', {
                    fontFamily: 'Arial',
                    fontSize: 18,
                    color: '#ffffff'
                }).setOrigin(0.5);

                closeButton.on('pointerup', () => {
                    // Remove all settings elements
                    overlay.destroy();
                    closeButton.destroy();
                    closeText.destroy();

                    // Find and destroy all settings texts
                    this.children.list.forEach(child => {
                        if (child.type === 'Text' &&
                            child.y >= 150 && child.y <= 350 &&
                            child !== this.children.list[0]) {
                            child.destroy();
                        }
                    });
                });
            });
        } catch (error) {
            console.error('Error creating settings button:', error);
        }
    }

    createButton(x, y, text, callback) {
        // Create button background
        const button = this.add.rectangle(x, y, 200, 50, 0x4CAF50);
        button.setInteractive({ useHandCursor: true });

        // Add button text
        const buttonText = this.add.text(x, y, text, {
            fontFamily: 'Arial',
            fontSize: 20,
            color: '#ffffff'
        }).setOrigin(0.5);

        // Add hover effect
        button.on('pointerover', () => {
            button.fillColor = 0x66BB6A;
        });

        button.on('pointerout', () => {
            button.fillColor = 0x4CAF50;
        });

        // Add click effect
        button.on('pointerdown', () => {
            button.fillColor = 0x388E3C;
        });

        button.on('pointerup', () => {
            button.fillColor = 0x4CAF50;
            callback();
        });

        return { button, text: buttonText };
    }

    startGame() {
        console.log('Starting game...');

        // Simple transition to the camera permission scene
        try {
            this.scene.start('CameraPermissionScene');
        } catch (error) {
            console.error('Error starting CameraPermissionScene:', error);

            // Show error message
            this.add.text(400, 450, 'Error starting game. Check console for details.', {
                fontFamily: 'Arial',
                fontSize: 16,
                color: '#ff0000',
                backgroundColor: '#000000',
                padding: { x: 10, y: 5 }
            }).setOrigin(0.5);
        }
    }

    showInstructions() {
        console.log('Showing instructions...');

        // Create a simple overlay with instructions
        const overlay = this.add.rectangle(400, 300, 600, 400, 0x000000, 0.9);

        // Add title
        this.add.text(400, 150, 'HOW TO PLAY', {
            fontFamily: 'Arial',
            fontSize: 28,
            color: '#ffffff'
        }).setOrigin(0.5);

        // Add instructions
        const instructions = [
            "Use your camera to perform hand gestures:",
            "• Flat Hand - Delivers a slap",
            "• Closed Fist - Throws a punch",
            "• Finger Guns - Fires a shot",
            "",
            "The faster your swing, the higher your score!",
            "Chain gestures together to build combos."
        ];

        let yPos = 200;
        instructions.forEach(line => {
            this.add.text(400, yPos, line, {
                fontFamily: 'Arial',
                fontSize: 18,
                color: '#ffffff',
                align: 'center'
            }).setOrigin(0.5);
            yPos += 30;
        });

        // Add close button
        const closeButton = this.add.rectangle(400, 450, 150, 40, 0xF44336);
        closeButton.setInteractive();

        const closeText = this.add.text(400, 450, 'Close', {
            fontFamily: 'Arial',
            fontSize: 18,
            color: '#ffffff'
        }).setOrigin(0.5);

        closeButton.on('pointerup', () => {
            // Remove all instruction elements
            overlay.destroy();
            closeButton.destroy();
            closeText.destroy();

            // Find and destroy all instruction texts
            this.children.list.forEach(child => {
                if (child.type === 'Text' &&
                    child.y >= 150 && child.y <= 400 &&
                    child !== this.children.list[0]) {
                    child.destroy();
                }
            });
        });
    }

    showCredits() {
        console.log('Showing credits...');

        try {
            // Create a simple overlay for now - will transition to CreditsScene later
            const overlay = this.add.rectangle(400, 300, 600, 400, 0x000000, 0.9);

            // Add title
            this.add.text(400, 150, 'CREDITS', {
                fontFamily: 'Arial',
                fontSize: 28,
                color: '#ffffff'
            }).setOrigin(0.5);

            // Add credits
            const credits = [
                "GAME DESIGN",
                "Politically Irreverent Studios",
                "",
                "DEVELOPMENT",
                "Lead Developer",
                "Hand Gesture Specialist",
                "",
                "SPECIAL THANKS",
                "All Political Figures Who Inspired Us",
                "The First Amendment"
            ];

            let yPos = 200;
            credits.forEach((line, index) => {
                const color = index % 3 === 0 ? '#ffff00' : '#ffffff';
                const fontSize = index % 3 === 0 ? 20 : 16;

                this.add.text(400, yPos, line, {
                    fontFamily: 'Arial',
                    fontSize: fontSize,
                    color: color,
                    align: 'center'
                }).setOrigin(0.5);

                yPos += line === "" ? 20 : 30;
            });

            // Add close button
            const closeButton = this.add.rectangle(400, 450, 150, 40, 0xF44336);
            closeButton.setInteractive();

            const closeText = this.add.text(400, 450, 'Close', {
                fontFamily: 'Arial',
                fontSize: 18,
                color: '#ffffff'
            }).setOrigin(0.5);

            closeButton.on('pointerup', () => {
                // Remove all credit elements
                overlay.destroy();
                closeButton.destroy();
                closeText.destroy();

                // Find and destroy all credit texts
                this.children.list.forEach(child => {
                    if (child.type === 'Text' &&
                        child.y >= 150 && child.y <= 450 &&
                        child !== this.children.list[0]) {
                        child.destroy();
                    }
                });
            });
        } catch (error) {
            console.error('Error showing credits:', error);
        }
    }

    startGame() {
        console.log('Starting game...');

        try {
            this.scene.start('CameraPermissionScene');
        } catch (error) {
            console.error('Error starting CameraPermissionScene:', error);

            // Show error message
            this.add.text(400, 480, 'Error starting game. Please try again.', {
                fontFamily: 'Arial',
                fontSize: 16,
                color: '#ff0000',
                backgroundColor: '#000000',
                padding: { x: 10, y: 5 }
            }).setOrigin(0.5);
        }
    }


    shutdown() {
        // Stop music
        if (this.soundManager) {
            this.soundManager.stopMusic();
        }
    }
}

export default MainMenuScene;